import { ConfirmButtonArray, FormikTextInput, SectionsSelect, Select, PageSpinner } from '@td-client-frontend/common';
import { Remove } from '@td-client-frontend/icons';
import { Form, Formik, FormikHelpers } from 'formik';
import { startCase } from 'lodash';
import React, { useCallback, useState } from 'react';
import { useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { useDebounce } from 'use-debounce';
import * as Yup from 'yup';
import { Edge, WithInfinityQuery } from '../../../@types/common';
import { ManageRoleType } from '../../../@types/Roles';

import { Button } from '../../../components/Button';
import { useAxios } from '../../../context/AxiosContext';
import { useOrgsList } from '../../../hooks/useOrgsList';
import useRoles from '../../../hooks/useRoles';
import { useUserData } from '../../../hooks/useUserData';
import { baseUserValidationSchema } from '../schemas/userValidationSchemas';
import { trimmingFields } from '../../../utils/normalizingUtils';

export interface ManageUserRoleTask {
  action: 'ADD' | 'REMOVE';
  roleId?: string;
  contextId?: number;
  contextType?: string;
}

interface UserRole {
  id: string;
  roleNm: string;
}

interface GrantedRole {
  grants: string[];
  id: string;
  roleNm: string;
  userCount: number;
}

interface GrantedScope {
  contractee: string | null;
  id: string | null;
  name: string;
  type: string | null;
}

interface GrantedPermissions {
  role: GrantedRole;
  scope: GrantedScope;
}

export const contextTypeMapping: Record<string, string> = {
  organization: 'Organization',
  // TODO this value should be "BenefitSponsor" but that requires a tas release
  benefitSponsor: 'Organization',
  contractee: 'Contractee',
  group: 'Group'
};

interface ManageUserFormikState {
  id: number;
  firstName?: string;
  lastName?: string;
  userName: string;
  email?: string;
  permissions: GrantedPermissions[];
}

export const ManageUserModal = ({ userId, hideModal }: { userId: number; hideModal: () => void }) => {
  const queryClient = useQueryClient();
  const { authAxios } = useAxios();
  const [orgSearchText, setOrgSearchText] = useState<string>('');
  const [currentRole, setCurrentRole] = useState<UserRole>();
  const [currentOrganization, setCurrentOrganization] = useState<any>(null);

  // debounce organization search
  const [debouncedOrgSearch] = useDebounce<string>(orgSearchText, 750);

  // data fetching for roles, user data, and org list
  const { data: roles, isFetching: isRoleFetching } = useRoles();
  const { data: userData, updateUser, isFetching } = useUserData({ userId });
  const orgsQuery = useOrgsList({
    search: debouncedOrgSearch.length ? debouncedOrgSearch : undefined
  });
  const { data: orgList } = orgsQuery;

  const [manageRolesTasks, setManageRolesTasks] = useState<ManageUserRoleTask[]>([]);

  // flatten roles for use in select dropdown -- only active roles
  const rolesList =
    roles?.pages?.reduce(
      (result: Edge<ManageRoleType>[], page: WithInfinityQuery<ManageRoleType>) =>
        result.concat(page?.edges?.filter(({ node }) => node?.active)),
      []
    ) ?? [];

  const deactivateUser = async (values: any) => {
    const { permissions, userName, ...rest } = values;
    await updateUser([
      {
        ...rest,
        permissions: undefined,
        active: false
      }
    ]).then(hideModal);
  };

  const resetPassword = async () => {
    try {
      const prefEmail = userData?.email.filter((email: any) => email.preferred)[0] || userData?.email[0];
      await authAxios.post('/request-password-reset-for-user', {
        userEmail: prefEmail?.emailAddress,
        username: userData?.userNm
      });
      toast.success('Successfully reset password for the user', { position: 'top-center' });
      queryClient.invalidateQueries('client-reps');
      hideModal();
    } catch (e) {
      toast.error('Failed to reset the password...', { position: 'top-center' });
    }
  };

  const manageUserRoles = useCallback(() => {
    return authAxios({
      method: 'POST',
      url: `/users/ClientRep/${userId}/role`,
      data: {
        roles: manageRolesTasks
      }
    });
  }, [authAxios, manageRolesTasks, userId]);

  const saveUser = async ({ permissions, userName, ...rest }: any) => {
    if (manageRolesTasks && manageRolesTasks.length) {
      try {
        const {
          data: { errors }
        } = await manageUserRoles();
        if (errors && errors.length) {
          errors.forEach((error: any) => {
            try {
              toast.error(JSON.parse(error).message);
            } catch (e) {
              toast.error(error);
            }
          });

          return;
        }
      } catch (error) {
        toast.error(error);
      }
    }

    return updateUser([{ id: String(userData.id), ...trimmingFields(rest) }]).then(hideModal);
  };

  const addButtonIsDisabled = !currentRole;
  const emailAddress =
    userData?.email?.find(({ emailAddress }: { emailAddress: string }) => emailAddress)?.emailAddress ?? '';

  if (isFetching || isRoleFetching) {
    return <PageSpinner />;
  }

  return (
    <Formik
      initialValues={
        {
          id: userData?.id,
          firstName: userData?.firstNm ?? '',
          lastName: userData?.lastNm ?? '',
          email: emailAddress,
          userName: userData?.userNm ?? '',
          permissions: userData?.permissions
        } as ManageUserFormikState
      }
      enableReinitialize
      validateOnBlur
      validateOnChange={false}
      validationSchema={Yup.object({
        ...baseUserValidationSchema(authAxios, emailAddress),
        userName: Yup.string().max(255, 'User name must be less than 255 characters').trim().required()
      })}
      onSubmit={async (values, { setSubmitting }: FormikHelpers<ManageUserFormikState>) => {
        setSubmitting(true);
        await saveUser(values);
        setSubmitting(false);
      }}
    >
      {({ isSubmitting, isValid, values, setFieldValue, handleSubmit, submitForm }) => {
        const addRelation = () => {
          const [type, contextId] = (currentOrganization || '').split('/');
          const { roleNm, id: roleId } = currentRole || {};
          setCurrentOrganization(null);
          setCurrentRole(undefined);
          setOrgSearchText('');

          setManageRolesTasks([
            ...manageRolesTasks,
            {
              action: 'ADD',
              roleId: roleId,
              contextId: contextId ? Number(contextId) : undefined,
              // @ts-ignore
              contextType: contextTypeMapping[type] || undefined
            }
          ]);

          setFieldValue('permissions', [
            ...values.permissions,
            {
              role: {
                id: roleId || undefined,
                roleNm: roleNm || undefined
              },
              scope: contextId ? orgList[type].find((org: { id: string }) => org.id === contextId) : {},
              pending: true
            }
          ]);
        };

        return (
          <Form data-testid="loginForm" className="relative flex flex-col w-full h-full" onSubmit={handleSubmit}>
            <div className="flex flex-col flex-grow overflow-y-auto">
              <div className="grid px-6 py-3">
                <div className="mb-3">
                  <h3 className="mb-2">User Details</h3>
                  <div className="flex justify-between pt-2 pb-4 text-center text-gray-50">
                    <p>Edit the user for which these permissions will apply.</p>
                    <p>* Required</p>
                  </div>

                  <div className="grid grid-cols-1 gap-0 sm:gap-4 sm:grid-cols-4">
                    <FormikTextInput
                      data-testid="firstNmInput"
                      label="First Name *"
                      name="firstName"
                      type="text"
                      placeholder="Please enter first name"
                    />

                    <FormikTextInput
                      data-testid="lastNmInput"
                      label="Last Name *"
                      name="lastName"
                      type="text"
                      placeholder="Please enter last name"
                    />

                    <FormikTextInput
                      data-testid="emailInput"
                      label="Email Address *"
                      name="email"
                      type="email"
                      placeholder="Please enter email address"
                    />

                    <FormikTextInput
                      data-testid="userNmInput"
                      label="User Name"
                      name="userName"
                      type="userName"
                      placeholder="Empty"
                      disabled
                    />
                  </div>
                </div>
              </div>

              <div className="p-6 pt-4 mt-2 border-opacity-25 border-t-1 border-gray-50">
                <h3 className="mb-2">Organizations and Roles</h3>
                <div className="flex justify-between pt-2 pb-4 text-center text-gray-50">
                  <p>Select the organization(s) and level of permission you want to assign.</p>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-74">
                    <SectionsSelect
                      name="organization"
                      options={
                        (orgList &&
                          Object.keys(orgList).map((key) => ({
                            label: startCase(key),
                            items: orgList[key].map(({ id, name, legacyGroupId }: any) => ({
                              value: `${key}/${id}`,
                              label: `${legacyGroupId !== undefined ? `(${legacyGroupId})` : ''} ${name}`
                            }))
                          }))) ??
                        []
                      }
                      isLoading={orgsQuery.isLoading}
                      onSearchTextChange={(value) => setOrgSearchText(value)}
                      value={currentOrganization}
                      label="Select Organization"
                      placeholder="Start typing the name..."
                      onChange={(value: any) => setCurrentOrganization(value)}
                      error=""
                      maxVisible={3}
                      isError={false}
                      required
                    />
                  </div>

                  <div className="w-74">
                    <Select
                      name="role"
                      options={rolesList.map(({ node }: any) => ({
                        label: node.roleNm,
                        value: node.id
                      }))}
                      value={currentRole ? currentRole?.id : null}
                      label="Select Role *"
                      placeholder="Please select roles ..."
                      onChange={(value) => setCurrentRole(rolesList.find(({ node }: any) => node.id === value)?.node)}
                      error={!currentRole ? 'The field is required' : undefined}
                      maxVisible={3}
                      required
                    />
                  </div>

                  {currentOrganization ? (
                    <Button className="mb-1" onClick={() => addRelation()} title="Add" disabled={addButtonIsDisabled} />
                  ) : (
                    <ConfirmButtonArray
                      id="add_user_confirm"
                      onClick={addRelation}
                      disabled={addButtonIsDisabled}
                      className="btn btn-primary mb-1"
                      tooltipOverrides={{
                        place: 'top',
                        arrowColor: '#5b2f91',
                        offset: {},
                        isModalConfirm: true
                      }}
                      customization={{
                        tooltipText:
                          'You are about to grant global permissions (i.e. access to all groups) for this user. Press continue to confirm to make changes or cancel only after checking with your supervisor.',
                        actionCancelButtonLabel: 'Cancel',
                        actionButtonLabel: 'Continue'
                      }}
                    >
                      Add
                    </ConfirmButtonArray>
                  )}
                </div>
                {values?.permissions?.length ? (
                  <div className="w-full max-h-full overflow-x-hidden overflow-y-auto">
                    <div className="h-48 my-2 overflow-x-auto font-normal">
                      <div className="grid mb-3 uppercase text-gray-30" style={{ gridTemplateColumns: '350px 1fr' }}>
                        <span>Organization</span>
                        <span>Role</span>
                      </div>
                      {values.permissions &&
                        values.permissions.map((perm: any, idx: number) => (
                          <div
                            key={`${perm.role.id}/${perm.scope.id}/${perm.scope.type}`}
                            className="grid items-center py-2 border-b"
                            style={{ gridTemplateColumns: '350px 1fr auto' }}
                          >
                            <span>{perm.scope.name || 'Global'}</span>
                            <span>{perm.role.roleNm}</span>
                            <ConfirmButtonArray
                              id={`removeRoleTooltip-${perm.role.id}-${perm.scope.id}`}
                              onClick={() => {
                                if (perm.pending) {
                                  setManageRolesTasks([
                                    ...manageRolesTasks.filter(({ action, roleId, contextId, contextType }) => {
                                      return (
                                        action !== 'ADD' &&
                                        roleId !== perm.role.id &&
                                        contextId !== perm.scope.id &&
                                        contextType !== perm.scope.type
                                      );
                                    })
                                  ]);
                                } else {
                                  setManageRolesTasks([
                                    ...manageRolesTasks,
                                    {
                                      action: 'REMOVE',
                                      roleId: perm.role.id,
                                      contextId: perm.scope.id || undefined,
                                      contextType: perm.scope.type || undefined
                                    }
                                  ]);
                                }

                                setFieldValue(
                                  'permissions',
                                  values.permissions.filter((permission, index) => index !== idx)
                                );
                              }}
                              tooltipOverrides={{
                                place: 'top',
                                arrowColor: '#5b2f91',
                                offset: {},
                                isModalConfirm: true
                              }}
                              customization={{
                                actionCancelButtonLabel: 'No',
                                actionButtonLabel: 'Yes'
                              }}
                              entityName="role"
                            >
                              <Remove className="cursor-pointer" />
                            </ConfirmButtonArray>
                          </div>
                        ))}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
            <div className="flex items-center justify-between p-6 rounded-b-lg sm:px-6 sm:flex bg-gray-10 border-t-1 border-gray-20">
              <div className="flex gap-4">
                <ConfirmButtonArray
                  id="modal_deactivate_confirm"
                  onClick={() => deactivateUser(values)}
                  tooltipOverrides={{
                    place: 'top',
                    arrowColor: '#5b2f91',
                    offset: {},
                    isModalConfirm: true
                  }}
                  className="btn btn-danger focus:outline-none focus-visible:ring"
                  customization={{
                    tooltipText: 'Are you sure you want to deactivate the user?',
                    actionButtonLabel: 'Confirm'
                  }}
                >
                  Deactivate
                </ConfirmButtonArray>
                <ConfirmButtonArray
                  id={`${values.id}-modal_reset_password_confirm`}
                  onClick={resetPassword}
                  tooltipOverrides={{
                    place: 'top',
                    arrowColor: '#5b2f91',
                    offset: {},
                    isModalConfirm: true
                  }}
                  className="btn btn-secondary focus:outline-none focus-visible:ring"
                  customization={{
                    tooltipText: 'Are you sure you want to reset the password for the user?',
                    actionButtonLabel: 'Yes, reset the password'
                  }}
                >
                  Reset password
                </ConfirmButtonArray>
              </div>

              <div className="flex gap-4">
                <Button kind="secondary" title="Cancel" onClick={hideModal} />
                <ConfirmButtonArray
                  id={`${values.id}-modal_save_confirm`}
                  className="btn btn-primary"
                  disabled={!values?.permissions?.length}
                  onClick={submitForm}
                  tooltipOverrides={{
                    place: 'top',
                    arrowColor: '#5b2f91',
                    offset: {},
                    isModalConfirm: true
                  }}
                  customization={{
                    tooltipText: 'Are you sure you want to save changes to this user?',
                    actionButtonLabel: 'Confirm'
                  }}
                >
                  Save
                </ConfirmButtonArray>
              </div>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};
